import React from 'react'
import type { SkeletonProps } from 'antd/es/skeleton'
import type { AvatarProps as SkeletonAvatarProps } from 'antd/es/skeleton/Avatar'
import type { SkeletonButtonProps } from 'antd/es/skeleton/Button'
import type { SkeletonImageProps } from 'antd/es/skeleton/Image'
import type { SkeletonInputProps } from 'antd/es/skeleton/Input'
import type { SkeletonNodeProps } from 'antd/es/skeleton/Node'

import { Skeleton as ShadcnSkeleton } from 'src/components/ui/skeleton'
import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Skeleton` seam. `./Skeleton.tsx` is the
 * dispatcher that chooses between this and `antd/es/skeleton`; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Skeleton.tsx`
 * (its Phase 2 slice 11). Divergences from the reference are marked
 * `[development]` below; everything else is the reference verbatim.
 *
 * The EXTERNAL API is antd's own `SkeletonProps` plus the `.Button` /
 * `.Input` / `.Avatar` / `.Node` / `.Image` element variants, so the 163 files
 * that already import `Skeleton` from `src/antd` switch with no edit at all.
 *
 * SHAPE IS THE CONTRACT, not the pulse. A skeleton stands in for content that
 * has not arrived, so every one of antd's shape defaults is transcribed rather
 * than approximated: the title's width depends on whether an avatar and a
 * paragraph are present (38% / 50%), the paragraph's row count depends on the
 * same two (2 or 3), and a single `paragraph.width` applies to the LAST row
 * only — the classic short-last-line look. Getting the pulse wrong is a
 * restyle; getting those wrong is a layout jump when the data lands.
 *
 * `active` is antd's shimmer opt-in and the shadcn primitive animates
 * unconditionally, so an INACTIVE skeleton explicitly turns the pulse off.
 * That keeps `active={false}` (antd's default) visually honest rather than
 * silently upgrading every placeholder to an animated one.
 *
 * [development] `.ant-skeleton*` IS NOT RE-EMITTED, and this is the one place
 * this port diverges structurally from the reference. Over there antd is
 * deleted, so `.ant-skeleton` can never be injected and re-using the names
 * costs nothing. Here antd is installed and the toggle is a RUNTIME flip: any
 * page that rendered an antd Skeleton before the flip has antd's skeleton
 * stylesheet on it, UNLAYERED, and `.ant-skeleton { display: table }` +
 * `.ant-skeleton-content { display: table-cell }` would then beat this
 * component's `flex`. That is the `.ant-btn` situation, not the `.ant-space`
 * one. So the parts are keyed by `data-slot` only, and the seven live
 * stylesheets that reached into antd's names each grew a `[data-slot='…']`
 * selector beside the `.ant-skeleton-…` one so both renderings stay covered
 * (the same treatment `../Tag` gave its three).
 * ------------------------------------------------------------------------- */

const AVATAR_SIZE_CLASS: Record<string, string> = {
  small: 'size-6',
  default: 'size-8',
  large: 'size-10',
}

const ELEMENT_SIZE_CLASS: Record<string, string> = {
  small: 'h-6',
  default: 'h-8',
  large: 'h-10',
}

const sizeStyle = (size: number | string | undefined) =>
  typeof size === 'number' ? { width: size, height: size } : undefined

const widthStyle = (width: number | string | undefined) =>
  width == null ? undefined : { width }

/** antd's default title width, which depends on what else is rendered. */
const defaultTitleWidth = (hasAvatar: boolean, hasParagraph: boolean) => {
  if (!hasAvatar && hasParagraph) return '38%'
  if (hasAvatar && hasParagraph) return '50%'
  return undefined
}

/** antd's default paragraph shape, which also depends on the siblings. */
const defaultParagraph = (hasAvatar: boolean, hasTitle: boolean) => ({
  rows: !hasAvatar && hasTitle ? 3 : 2,
  width: !hasAvatar || !hasTitle ? '61%' : undefined,
})

const Bar = ({
  active,
  className,
  style,
  slot,
}: {
  active?: boolean
  className?: string
  style?: React.CSSProperties
  slot: string
}) => (
  <ShadcnSkeleton
    data-slot={slot}
    className={cn(!active && 'animate-none', className)}
    style={style}
  />
)

/** The shared element wrapper — antd's `.ant-skeleton.ant-skeleton-element`. */
const SkeletonElement = ({
  active,
  block,
  className,
  style,
  children,
}: {
  active?: boolean
  block?: boolean
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}) => (
  <div
    data-slot="skeleton-element"
    data-active={active ? '' : undefined}
    className={cn(block ? 'block w-full' : 'inline-block', className)}
    style={style}
  >
    {children}
  </div>
)

const SkeletonBase = (props: SkeletonProps) => {
  const {
    active,
    loading,
    avatar,
    title = true,
    paragraph = true,
    round,
    className,
    style,
    children,
  } = props

  // antd renders the placeholder only when `loading || !('loading' in props)`,
  // so an explicitly-passed-but-undefined `loading` renders the children rather
  // than sticking on a permanent placeholder. The key's PRESENCE is what
  // decides, which is why `props` is read un-destructured here.
  if ('loading' in props && !loading) return <>{children}</>

  const hasAvatar = !!avatar
  const hasTitle = !!title
  const hasParagraph = !!paragraph

  const avatarProps = typeof avatar === 'object' ? avatar : {}
  const titleProps = typeof title === 'object' ? title : {}
  const paragraphProps = typeof paragraph === 'object' ? paragraph : {}

  const paragraphDefaults = defaultParagraph(hasAvatar, hasTitle)
  const rows = paragraphProps.rows ?? paragraphDefaults.rows
  const rowWidth = paragraphProps.width ?? paragraphDefaults.width

  const avatarSize = avatarProps.size ?? 'default'
  const avatarShape = avatarProps.shape ?? 'circle'

  return (
    <div
      data-slot="skeleton-root"
      data-active={active ? '' : undefined}
      className={cn('flex gap-4', className)}
      style={style}
    >
      {hasAvatar ? (
        <div data-slot="skeleton-header" className="shrink-0">
          <Bar
            slot="skeleton-avatar"
            active={active}
            className={cn(
              typeof avatarSize === 'string'
                ? AVATAR_SIZE_CLASS[avatarSize]
                : undefined,
              avatarShape === 'circle' ? 'rounded-full' : 'rounded-md'
            )}
            // AvatarSize also admits a responsive breakpoint map, which is
            // not a px value; only the scalar forms size the box.
            style={sizeStyle(
              typeof avatarSize === 'object' ? undefined : avatarSize
            )}
          />
        </div>
      ) : null}

      <div data-slot="skeleton-content" className="w-full min-w-0 space-y-3">
        {hasTitle ? (
          <Bar
            slot="skeleton-title"
            active={active}
            className={cn('h-4', round && 'rounded-full')}
            style={
              widthStyle(
                titleProps.width ?? defaultTitleWidth(hasAvatar, hasParagraph)
              ) ?? { width: '100%' }
            }
          />
        ) : null}

        {hasParagraph ? (
          <ul
            data-slot="skeleton-paragraph"
            className="m-0 list-none space-y-3 p-0"
          >
            {Array.from({ length: rows }).map((_, i) => {
              // antd applies an array width per row; a single width applies to
              // the LAST row only (the classic "short last line" look).
              const isLastRow = i === rows - 1
              let width: number | string | undefined
              if (Array.isArray(rowWidth)) width = rowWidth[i]
              else if (isLastRow) width = rowWidth
              return (
                // Rows are positional and have no identity of their own.
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>
                  <Bar
                    slot="skeleton-paragraph-row"
                    active={active}
                    className={cn('h-4 w-full', round && 'rounded-full')}
                    style={widthStyle(width)}
                  />
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>
    </div>
  )
}

const SkeletonButton = ({
  active,
  size = 'default',
  shape,
  block,
  className,
  style,
}: SkeletonButtonProps) => (
  <SkeletonElement
    active={active}
    block={block}
    className={className}
    style={style}
  >
    <Bar
      slot="skeleton-button"
      active={active}
      className={cn(
        ELEMENT_SIZE_CLASS[size] ?? ELEMENT_SIZE_CLASS.default,
        block ? 'w-full' : 'w-16',
        shape === 'circle' || shape === 'round' ? 'rounded-full' : undefined
      )}
    />
  </SkeletonElement>
)

const SkeletonInput = ({
  active,
  size = 'default',
  block,
  className,
  style,
}: SkeletonInputProps) => (
  <SkeletonElement
    active={active}
    block={block}
    className={className}
    style={style}
  >
    <Bar
      slot="skeleton-input"
      active={active}
      className={cn(
        ELEMENT_SIZE_CLASS[size] ?? ELEMENT_SIZE_CLASS.default,
        block ? 'w-full' : 'w-40'
      )}
    />
  </SkeletonElement>
)

const SkeletonAvatar = ({
  active,
  size = 'default',
  shape = 'circle',
  className,
  style,
}: SkeletonAvatarProps) => (
  <SkeletonElement active={active} className={className} style={style}>
    <Bar
      slot="skeleton-avatar"
      active={active}
      className={cn(
        typeof size === 'string' ? AVATAR_SIZE_CLASS[size] : undefined,
        shape === 'circle' ? 'rounded-full' : 'rounded-md'
      )}
      style={sizeStyle(typeof size === 'object' ? undefined : size)}
    />
  </SkeletonElement>
)

const SkeletonNode = ({
  active,
  className,
  style,
  children,
}: SkeletonNodeProps) => (
  <SkeletonElement active={active} className={className} style={style}>
    {/* antd's Node hosts arbitrary content INSIDE the placeholder box. */}
    <ShadcnSkeleton
      data-slot="skeleton-node"
      className={cn(
        'flex size-24 items-center justify-center',
        !active && 'animate-none'
      )}
    >
      {children}
    </ShadcnSkeleton>
  </SkeletonElement>
)

/**
 * antd's `Skeleton.Image` — a fixed placeholder box with a picture glyph
 * inside. No call site in this repo uses it (measured: 0), but it dispatches
 * like every other static so the seam cannot end up half-switched the day one
 * appears. The glyph is drawn inline rather than taken from the icon seam:
 * antd draws its own path here too, and it is decoration inside a placeholder.
 */
const SkeletonImage = ({ active, className, style }: SkeletonImageProps) => (
  <SkeletonElement active={active} className={className} style={style}>
    <ShadcnSkeleton
      data-slot="skeleton-image"
      className={cn(
        'flex h-24 w-24 items-center justify-center',
        !active && 'animate-none'
      )}
    >
      <svg
        viewBox="0 0 1098 1024"
        width="48"
        height="48"
        fill="currentColor"
        className="text-muted-foreground/40"
        aria-hidden
      >
        <path d="M365.714286 329.142857q0 45.714286-32.036571 77.677714t-77.677714 32.036571-77.677714-32.036571-32.036571-77.677714 32.036571-77.677714 77.677714-32.036571 77.677714 32.036571 32.036571 77.677714zM950.857143 548.571429l0 256-804.571429 0 0-109.714286 182.857143-182.857143 91.428571 91.428571 292.571429-292.571429zM1005.714286 146.285714l-914.285714 0q-7.460571 0-12.873143 5.412571t-5.412571 12.873143l0 694.857143q0 7.460571 5.412571 12.873143t12.873143 5.412571l914.285714 0q7.460571 0 12.873143-5.412571t5.412571-12.873143l0-694.857143q0-7.460571-5.412571-12.873143t-12.873143-5.412571zM1097.142857 164.571429l0 694.857143q0 37.741714-26.843429 64.585143t-64.585143 26.843429l-914.285714 0q-37.741714 0-64.585143-26.843429t-26.843429-64.585143l0-694.857143q0-37.741714 26.843429-64.585143t64.585143-26.843429l914.285714 0q37.741714 0 64.585143 26.843429t26.843429 64.585143z" />
      </svg>
    </ShadcnSkeleton>
  </SkeletonElement>
)

export const Skeleton = Object.assign(SkeletonBase, {
  Button: SkeletonButton,
  Input: SkeletonInput,
  Avatar: SkeletonAvatar,
  Node: SkeletonNode,
  Image: SkeletonImage,
})
